import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, Star, Zap, TrendingUp, Loader2, Bot, Lock, Sparkles, RefreshCw, Clock, Gift } from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Membership = () => {
  const navigate = useNavigate();
  const { country, countryConfig: ctxCountryConfig, currencyConfig } = useCountry();
  
  const [plans, setPlans] = useState([]);
  const [comingSoonPlans, setComingSoonPlans] = useState([]);
  const [ppuOptions, setPpuOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [aiAccess, setAiAccess] = useState(null);
  const [countryPlanConfig, setCountryPlanConfig] = useState(null);

  useEffect(() => {
    fetchCountryPlans();
    fetchPpuOptions();
    fetchAiAccess();
    // Get current user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
  }, [country]);

  const fetchCountryPlans = async () => {
    try {
      setLoading(true);
      // Fetch country-specific plans
      const response = await fetch(`${API_URL}/api/membership/country/${country}/plans`);
      const data = await response.json();
      
      if (data.success) {
        setPlans(data.plans || []);
        setComingSoonPlans(data.coming_soon_plans || []);
        setCountryPlanConfig({
          currency_code: data.currency_code,
          currency_symbol: data.currency_symbol,
          promo_enabled: data.promo_enabled,
          promo_end_date: data.promo_end_date
        });
      } else {
        // Fallback to regular plans
        const fallbackResponse = await fetch(`${API_URL}/api/membership/plans`);
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.success) {
          setPlans(fallbackData.plans);
        } else {
          setError('Failed to load membership plans');
        }
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load membership plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchPpuOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/membership/pay-per-use`);
      const data = await response.json();
      if (data.success) {
        setPpuOptions(data.options);
      }
    } catch (err) {
      console.error('Error fetching PPU options:', err);
    }
  };

  const fetchAiAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/membership/ai-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAiAccess(data);
      }
    } catch (err) {
      console.error('Error fetching AI access:', err);
    }
  };

  const handleSelectPlan = (plan) => {
    // Check if user is logged in
    if (!currentUser) {
      // Store intended plan and redirect to login
      localStorage.setItem('intendedPlan', JSON.stringify(plan));
      navigate('/login', { state: { from: '/membership', message: 'Please login to purchase a membership plan' } });
      return;
    }

    // Use promo price if available and promo is enabled
    const price = countryPlanConfig?.promo_enabled && plan.promo_price !== null && plan.promo_days > 0
      ? plan.promo_price
      : plan.regular_price;

    if (price === 0) {
      alert('You are already on this plan!');
      return;
    }
    
    // Navigate to payment method selection with plan details
    navigate('/payment/select-method', {
      state: {
        serviceType: `membership_${plan.tier_id}`,
        serviceDuration: plan.billing_cycle === 'yearly' ? 365 : 30,
        amount: price,
        serviceTitle: `${plan.name} Membership Plan`,
        planDetails: {
          ...plan,
          price: price,
          currency_symbol: countryPlanConfig?.currency_symbol || currencyConfig?.symbol
        }
      }
    });
  };

  const handlePurchasePpu = (option) => {
    if (!currentUser) {
      navigate('/login', { state: { from: '/membership', message: 'Please login to purchase AI credits' } });
      return;
    }
    
    navigate('/payment/select-method', {
      state: {
        serviceType: `ppu_${option.option_id}`,
        amount: option.price,
        serviceTitle: option.name,
        ppuDetails: option
      }
    });
  };

  const getPlanIcon = (tierName) => {
    const name = tierName?.toLowerCase();
    switch (name) {
      case 'premium':
        return <Star className="h-6 w-6" />;
      case 'basic':
        return <Zap className="h-6 w-6" />;
      case 'pro':
        return <TrendingUp className="h-6 w-6" />;
      default:
        return <TrendingUp className="h-6 w-6" />;
    }
  };

  const formatPrice = (price, symbol) => {
    if (price === null || price === undefined) return '';
    if (price === 0) return 'Free';
    return `${symbol}${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading membership plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchCountryPlans}>Try Again</Button>
        </div>
      </div>
    );
  }

  const currencySymbol = countryPlanConfig?.currency_symbol || currencyConfig?.symbol || 'Rs.';
  const promoEnabled = countryPlanConfig?.promo_enabled;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Boost your ads with AI-powered writing and reach more buyers
          </p>
          {promoEnabled && (
            <div className="mt-4">
              <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 text-sm">
                <Gift className="h-4 w-4 mr-2 inline" />
                Special Launch Offer - Limited Time Pricing!
              </Badge>
            </div>
          )}
        </div>

        {/* Current AI Access Status */}
        {aiAccess && (
          <div className="max-w-4xl mx-auto mb-12 px-4">
            <Card className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Bot className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Your AI Access</h3>
                      <p className="text-purple-100 text-sm">
                        {aiAccess.membership_type?.charAt(0).toUpperCase() + aiAccess.membership_type?.slice(1)} Plan
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{aiAccess.ai_ads_remaining}</p>
                      <p className="text-purple-100 text-xs">AI Ads Left</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold">{aiAccess.available_tones?.length || 0}</p>
                      <p className="text-purple-100 text-xs">Tones Available</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold">{aiAccess.free_regens_per_ad || 3}</p>
                      <p className="text-purple-100 text-xs">Free Regens/Ad</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold">{aiAccess.extra_regenerations || 0}</p>
                      <p className="text-purple-100 text-xs">Extra Regens</p>
                    </div>
                  </div>
                </div>
                
                {/* Tone visualization */}
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm text-purple-100 mb-2">Your available tones:</p>
                  <div className="flex flex-wrap gap-2">
                    {aiAccess.all_tones?.map(tone => {
                      const isAvailable = aiAccess.available_tones?.includes(tone);
                      return (
                        <Badge 
                          key={tone}
                          className={`text-xs ${
                            isAvailable 
                              ? 'bg-white text-purple-600' 
                              : 'bg-white/20 text-white/60'
                          }`}
                        >
                          {!isAvailable && <Lock className="h-3 w-3 mr-1" />}
                          {tone}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
          {plans.map((plan) => {
            const hasPromo = promoEnabled && plan.promo_price !== null && plan.promo_days > 0;
            const displayPrice = hasPromo ? plan.promo_price : plan.regular_price;
            
            return (
              <Card
                key={plan.tier_id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                  plan.is_recommended
                    ? 'border-4 border-blue-500 shadow-xl scale-105'
                    : 'border-2'
                }`}
              >
                {plan.is_recommended && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 text-sm font-semibold">
                    Recommended
                  </div>
                )}
                {hasPromo && (
                  <div className="absolute top-0 left-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 text-sm font-semibold rounded-br-lg">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {plan.promo_label}
                  </div>
                )}
                <CardHeader className="text-center pt-8">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                    plan.is_recommended
                      ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white'
                      : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600'
                  }`}>
                    {getPlanIcon(plan.name)}
                  </div>
                  <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                  {plan.description && (
                    <p className="text-gray-500 text-sm mb-2">{plan.description}</p>
                  )}
                  <div className="mb-4">
                    {hasPromo && plan.regular_price > displayPrice && (
                      <span className="text-gray-400 line-through text-lg mr-2">
                        {formatPrice(plan.regular_price, currencySymbol)}
                      </span>
                    )}
                    <span className={`text-5xl font-bold ${hasPromo ? 'text-green-600' : ''}`}>
                      {displayPrice === 0 ? 'Free' : formatPrice(displayPrice, currencySymbol)}
                    </span>
                    {displayPrice > 0 && (
                      <span className="text-gray-500">
                        /{plan.billing_cycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    )}
                  </div>
                  
                  {/* AI Features highlights */}
                  <div className="flex justify-center gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {plan.ai_ads_limit === -1 ? '∞' : plan.ai_ads_limit}
                      </p>
                      <p className="text-xs text-gray-500">AI Ads</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{plan.tone_count || 2}</p>
                      <p className="text-xs text-gray-500">Tones</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{plan.free_regenerations || 3}</p>
                      <p className="text-xs text-gray-500">Regens</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-8">
                    {plan.features?.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${
                      plan.is_recommended
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                        : ''
                    }`}
                    variant={plan.is_recommended ? 'default' : 'outline'}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    {displayPrice === 0 ? 'Get Started Free' : 'Select Plan'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}

          {/* Coming Soon Plans - Gift Wrapped Teaser */}
          {comingSoonPlans.map((plan) => (
            <Card
              key={plan.tier_id}
              className="relative overflow-hidden border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50"
            >
              {/* Gift Wrap Ribbon Effect */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {/* Vertical ribbon */}
                <div className="absolute left-1/2 -translate-x-1/2 w-12 h-full bg-gradient-to-b from-red-400 via-red-500 to-red-400 opacity-25"></div>
                {/* Horizontal ribbon */}
                <div className="absolute top-1/2 -translate-y-1/2 w-full h-12 bg-gradient-to-r from-red-400 via-red-500 to-red-400 opacity-25"></div>
              </div>

              {/* Content */}
              <div className="relative flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
                {/* Big Gift Icon */}
                <div className="w-24 h-24 mb-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <Gift className="h-12 w-12 text-white" />
                </div>
                
                {/* Plan Name */}
                <h3 className="text-3xl font-bold text-amber-800 mb-4">{plan.name}</h3>
                
                {/* Coming Soon Badge */}
                <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white px-6 py-3 rounded-full font-bold text-lg shadow-md mb-6">
                  <Gift className="h-5 w-5 inline mr-2" />
                  Coming Soon
                  <Gift className="h-5 w-5 inline ml-2" />
                </div>
                
                {/* Teaser Message */}
                <p className="text-amber-600 text-sm max-w-xs">
                  Something special is on the way! Stay tuned for our premium offering.
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Pay-Per-Use Section */}
        {ppuOptions.length > 0 && (
          <div className="max-w-4xl mx-auto mt-20 px-4">
            <div className="text-center mb-8">
              <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-300">
                Extra AI Credits
              </Badge>
              <h2 className="text-3xl font-bold mb-3">Exceeded Your Plan Limit?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                When you've used all your monthly AI ads, you can purchase additional credits. 
                Each credit allows you to create <strong>one extra AI-powered advertisement</strong> with your choice of tones.
              </p>
            </div>
            
            {/* Explanation box */}
            <Card className="mb-8 border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-1">How Extra Credits Work</h4>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• <strong>Same tones as your plan</strong> - Extra credits use the same tone options available in your current membership</li>
                      <li>• <strong>Pay per ad</strong> - Only pay when you need more ads beyond your plan limit</li>
                      <li>• <strong>Upgrade for more tones</strong> - Want access to all tones? Consider upgrading to Pro</li>
                      <li>• <strong>No expiry</strong> - Purchased credits don't expire until used</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {ppuOptions.map((option) => (
                <Card key={option.option_id} className="border-2 hover:border-purple-400 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-purple-600" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{option.name}</h3>
                    <p className="text-gray-500 text-sm mb-4">{option.description}</p>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-purple-600">{currencySymbol}{option.price}</span>
                      <span className="text-gray-500 text-sm">/ad</span>
                    </div>
                    <div className="mb-4 p-2 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-2">
                        <span className="font-semibold">{option.tone_count}</span> tones included:
                      </p>
                      <div className="flex flex-wrap justify-center gap-1">
                        {option.available_tones?.map(tone => (
                          <Badge key={tone} variant="outline" className="text-xs">
                            {tone}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full hover:bg-purple-50 hover:border-purple-400"
                      onClick={() => handlePurchasePpu(option)}
                    >
                      Buy Extra Credit
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Tip for upgrading */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Tip: If you frequently need extra ads, upgrading your plan is more cost-effective!
              </p>
            </div>
          </div>
        )}

        {/* Additional Benefits */}
        <div className="max-w-4xl mx-auto mt-20 px-4">
          <Card className="bg-gradient-to-br from-blue-600 to-purple-600 text-white border-0">
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold text-center mb-8">Why Upgrade?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Increased Visibility</h3>
                    <p className="text-blue-100 text-sm">Your ads appear at the top of search results</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Faster Sales</h3>
                    <p className="text-blue-100 text-sm">Premium ads sell 3x faster on average</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Featured Badge</h3>
                    <p className="text-blue-100 text-sm">Stand out with a premium badge on your listings</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Priority Support</h3>
                    <p className="text-blue-100 text-sm">Get help whenever you need it</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Membership;
